import axios from 'axios';
import api from './api';

export type AudioKind = 'SoundEffect' | 'Music';

export interface ProjectAssetAudio {
  kind: AudioKind;
  durationSeconds: number;
  loopable: boolean;
}

export interface ProjectAsset {
  assetId: string;
  name: string;
  description: string;
  assetType: string;
  fileExtension: string;
  fileSizeBytes: number;
  audio: ProjectAssetAudio | null;
}

export interface UserAssetUpload {
  assetId: string;
  name: string;
  description: string;
}

export interface UserAudioConstraints {
  sourceFileExtensions: string[];
  maxSourceBytes: number;
  maxSoundEffectSeconds: number;
  minMusicSeconds: number;
  maxMusicSeconds: number;
}

export interface UserAssetConstraints {
  maxFileSizeBytes: number;
  allowedFileExtensions: string[];
  namePattern: string;
  maxNameLength: number;
  maxDescriptionLength: number;
  maxAssetsPerProject: number;
  audio: UserAudioConstraints;
}

export interface AssetUploadOptions {
  licenseText?: string;
  audioKind?: AudioKind;
  loopable?: boolean;
}

export interface ProvidedAsset {
  assetId: string;
  name: string;
  description: string;
}

export async function getUserAssetConstraints(): Promise<UserAssetConstraints> {
  const { data } = await api.get<UserAssetConstraints>('user-assets/constraints');
  return data;
}

function assetFormData(name: string, description: string, file: File, options: AssetUploadOptions): FormData {
  const formData = new FormData();
  formData.append('name', name);
  formData.append('description', description);
  formData.append('file', file);
  if (options.licenseText) formData.append('licenseText', options.licenseText);
  if (options.audioKind) formData.append('audioKind', options.audioKind);
  if (options.loopable !== undefined) formData.append('loopable', String(options.loopable));
  return formData;
}

export async function uploadUserAsset(
  name: string,
  description: string,
  file: File,
  options: AssetUploadOptions = {},
): Promise<UserAssetUpload> {
  const { data } = await api.post<UserAssetUpload>('user-assets', assetFormData(name, description, file, options), {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function uploadProjectAsset(
  projectId: string,
  name: string,
  description: string,
  file: File,
  options: AssetUploadOptions = {},
): Promise<UserAssetUpload> {
  const { data } = await api.post<UserAssetUpload>(
    `project/${projectId}/assets`,
    assetFormData(name, description, file, options),
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function listProjectAssets(projectId: string): Promise<ProjectAsset[]> {
  const { data } = await api.get<ProjectAsset[]>(`project/${projectId}/assets`);
  return data;
}

export async function deleteProjectAsset(projectId: string, assetId: string): Promise<void> {
  await api.delete(`project/${projectId}/assets/${assetId}`);
}

export async function fetchProjectAssetObjectUrl(projectId: string, assetId: string): Promise<string> {
  const { data } = await api.get<Blob>(`project/${projectId}/assets/${assetId}/content`, {
    responseType: 'blob',
  });
  return URL.createObjectURL(data);
}

const ERRORS_WITH_MESSAGE = ['invalid_asset', 'audio_unavailable'];

export function getAssetValidationMessage(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const body = error.response?.data as { error?: unknown; message?: unknown } | undefined;
  if (typeof body?.error !== 'string' || !ERRORS_WITH_MESSAGE.includes(body.error)) return null;
  return typeof body.message === 'string' ? body.message : null;
}
